import React, { useState } from 'react';
import { TeamInvitation } from '../../types/team.types';
import { toast } from 'react-hot-toast';
import { TeamService } from '../../services/team.service';
import { ExclamationCircleIcon, XCircleIcon, EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useVault } from '../../contexts/VaultContext';
import { useAppSelector } from '../../hooks/useRedux';
import Button from '../common/Button';
import ConfirmModal from '../common/ConfirmModal';
import { hasPermission } from '../../utils/permissionUtils';
interface PendingUsersListProps {
    invitations: TeamInvitation[];
    onRevoke: () => void;
    onOpenInviteModal: () => void;
    entityType?: string;
}

const PendingUsersList: React.FC<PendingUsersListProps> = ({ invitations, onRevoke, onOpenInviteModal, entityType = 'Team' }) => {
    const { searchQuery } = useVault();
    const filteredInvitations = invitations.filter((invitation) => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
            invitation.email?.toLowerCase().includes(searchLower) ||
            invitation.invited_by_email?.toLowerCase().includes(searchLower) ||
            invitation.role_name?.toLowerCase().includes(searchLower)
        );
    });
    const { permissions } = useAppSelector((state) => state.auth);
    const hasRevokeInvitationsPermission = hasPermission(permissions, 'team', 'remove_members');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedInvitationId, setSelectedInvitationId] = useState<string | null>(null);
    const [isRevoking, setIsRevoking] = useState(false);

    const handleRevokeClick = (invitationId: string) => {
        setSelectedInvitationId(invitationId);
        setShowConfirmModal(true);
    };

    const confirmRevoke = async () => {
        if (!selectedInvitationId) return;
        
        try {
            setIsRevoking(true);
            await TeamService.revokeInvitation(selectedInvitationId);
            toast.success('Invitation revoked successfully');
            onRevoke();
        } catch (error) {
            console.error('Error revoking invitation:', error);
            toast.error('Failed to revoke invitation');
        } finally {
            setIsRevoking(false);
            setShowConfirmModal(false);
            setSelectedInvitationId(null);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow-xl overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
            {filteredInvitations.length === 0 ? (
                <div className="text-center py-16 px-6">
                    <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                              rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                        <EnvelopeIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No pending invitations</h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                        Invitations you send will appear here until they're accepted.
                    </p>
                </div>
            ) : (
                <>
                    {/* Desktop view */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Invitee</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Invited By</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Role</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Sent</th>
                                    {hasRevokeInvitationsPermission && (
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredInvitations.map((invitation) => (
                                    <tr key={invitation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                                        <td className="px-6 py-5">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 w-10 h-10">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center">
                                                        <span className="text-sm font-semibold text-white">
                                                            {invitation.email?.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{invitation.email}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">Pending invitation</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">{invitation.invited_by_email}</span>
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                                                {invitation.role_name}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                                {new Date(invitation.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </span>
                                        </td>

                                        {hasRevokeInvitationsPermission && (
                                        <td className="px-6 py-5 text-right">
                                            <button
                                                onClick={() => handleRevokeClick(invitation.id)}
                                                className="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 rounded-lg transition-colors text-sm font-medium"
                                            >
                                                <XMarkIcon className="h-4 w-4 mr-1" />
                                                Revoke
                                            </button>
                                        </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile view */}
                    <div className="block md:hidden">
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredInvitations.map((invitation) => (
                                <div key={invitation.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0 w-10 h-10">
                                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-semibold text-white">
                                                    {invitation.email?.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{invitation.email}</p>
                                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 font-medium">
                                                            {invitation.role_name}
                                                        </span>
                                                        <span className="text-gray-500 dark:text-gray-400">
                                                            Pending invitation
                                                        </span>
                                                    </div>
                                                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                        <span>Invited by: {invitation.invited_by_email}</span>
                                                    </div>
                                                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                        <span>Sent: {new Date(invitation.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                                    </div>
                                                </div>
                                                {hasRevokeInvitationsPermission && (
                                                    <button
                                                        onClick={() => handleRevokeClick(invitation.id)}
                                                        className="ml-4 inline-flex items-center p-1.5 bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 rounded-lg transition-colors"
                                                        aria-label="Revoke invitation"
                                                    >
                                                        <XCircleIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={confirmRevoke}
                title={`Revoke ${entityType} Invitation`}
                message="Are you sure you want to revoke this invitation? The user will no longer be able to join the team."
                confirmText="Revoke Invitation"
                isLoading={isRevoking}
            />
        </div>
    );
};

export default PendingUsersList; 